// RADAR DEL CENTRO V3 — vista institucional (/centro-v3 y /centro-v3/:sitioId).
// Datos reales (joins en frontend) o ESCENARIO DEMO (frontend-only). NUNCA escribe al backend.
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { getAlarmas, textoListaIncompleta } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useScenario } from "@/context/ScenarioContext";
import ScenarioSwitch from "@/components/ScenarioSwitch";
import { construirVista } from "@/lib/centroV3/data";
import CentroHeader from "@/components/centroV3/CentroHeader";
import AtencionList from "@/components/centroV3/AtencionList";
import CentroGrid from "@/components/centroV3/CentroGrid";
import CoberturasResumen from "@/components/centroV3/CoberturasResumen";
import PersonaDrawer from "@/components/persona/PersonaDrawer";
import PersonaQuickSearch from "@/components/persona/PersonaQuickSearch";
import RadarOperativo from "@/pages/RadarOperativo";
import RadarInstitucional from "@/pages/RadarInstitucional";
import MosaicoEkh from "@/components/ekh/MosaicoEkh";
import { snapshotInstitucional, snapshotResidencial } from "@/lib/demo/radarDemo";
import { capacidades } from "@/lib/capacidades";
import { Radar, ChevronRight, Radio, WifiOff, FlaskConical } from "lucide-react";

const useNow = () => { const [n, s] = useState(Date.now()); useEffect(() => { const t = setInterval(() => s(Date.now()), 1000); return () => clearInterval(t); }, []); return n; };
const esComercial = (s) => !!s.cliente_id;

function BarraControl({ children }) {
  return (
    <div className="sticky top-0 z-20 border-b border-slate-200 bg-white/95 backdrop-blur">
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-2.5">{children}</div>
    </div>
  );
}

function Selector({ institucional = false }) {
  const { isSupervisor } = useAuth();
  const navigate = useNavigate();
  const [sitios, setSitios] = useState([]);
  const [verLab, setVerLab] = useState(false);
  const [fallo, setFallo] = useState(false);
  useEffect(() => {
    // ATN-01 · M7 — la lista EFECTIVA ya viene acotada por el backend. Los sitios inactivos no son
    // destinos válidos del radar; un ERROR de consulta no se confunde con «cero centros» y nunca
    // se cae a un catálogo global.
    api.get("/sitios").then(({ data }) => { setSitios((data || []).filter((s) => s.estado !== "Inactivo")); setFallo(false); })
      .catch(() => { setSitios([]); setFallo(true); });
  }, []);
  // Un solo centro autorizado y activo: se entra directo por la ruta existente (sin selector inútil).
  useEffect(() => {
    if (!fallo && sitios.length === 1) navigate(`/centro-v3/${sitios[0].id}`, { replace: true });
  }, [fallo, sitios, navigate]);
  // RADAR-001: `cliente_id` SÓLO viaja al supervisor (el DTO externo no lo incluye), así que
  // clasificar por ese campo ocultaba TODOS los sitios autorizados de un usuario institucional.
  // El filtro comercial/laboratorio es una ayuda interna del supervisor, no un filtro de permisos:
  // el alcance ya lo aplica el backend dentro del query de /sitios.
  const puedeClasificar = isSupervisor;
  const laboratorio = puedeClasificar ? sitios.filter((s) => !esComercial(s)) : [];
  // RADAR-003: el conjunto listado y el contador se calculan UNA sola vez y se usan en ambos lugares.
  const visibles = !puedeClasificar || verLab ? sitios : sitios.filter(esComercial);
  return (
    <div className="mx-auto max-w-3xl p-5 sm:p-8">
      <div className="flex items-start justify-between gap-3">
        <div>
          {institucional ? (
            <>
              <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-[#012472]">Galarya Care</p>
              <h1 data-testid="centro-sel-titulo-inst" className="flex flex-wrap items-center gap-x-2 text-xl font-extrabold text-[#012472] sm:text-2xl">
                <Radar className="h-6 w-6 shrink-0" /> <span className="whitespace-nowrap">Radar Operativo</span> <span className="font-semibold text-slate-400">· Institucional</span>
              </h1>
              <p className="mt-1 text-sm text-slate-500">Elige el centro que vas a monitorear.</p>
            </>
          ) : (
            <>
              <h1 className="flex items-center gap-2 text-2xl font-extrabold text-[#012472]"><Radar className="h-6 w-6" /> Radar del Centro V3</h1>
              <p className="mt-1 text-sm text-slate-500">Elige un centro para ver el radar institucional (un sitio por pantalla).</p>
            </>
          )}
          <p data-testid="centro-sel-contador" className="mt-2 text-xs font-semibold text-slate-600">
            {visibles.length} {visibles.length === 1 ? "centro" : "centros"} en esta lista
            {puedeClasificar && laboratorio.length > 0 && !verLab && (
              <span className="font-normal text-slate-400"> · {laboratorio.length} de laboratorio oculto(s)</span>
            )}
          </p>
        </div>
        {isSupervisor && laboratorio.length > 0 && (
          <button data-testid="centro-toggle-lab" onClick={() => setVerLab((v) => !v)}
            className="mt-1 shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-500 hover:border-[#9B2C2C]/40 hover:text-[#9B2C2C]">
            <FlaskConical className="h-3.5 w-3.5" /> {verLab ? `Ocultar laboratorio (${laboratorio.length})` : `Ver laboratorio (${laboratorio.length})`}
          </button>
        )}
      </div>
      <div className="mt-6 grid gap-2.5 sm:grid-cols-2">
        {visibles.map((s) => {
          // La marca «laboratorio» es una ayuda INTERNA del supervisor (se deduce de `cliente_id`,
          // campo que el DTO externo no incluye): nunca se muestra a una cuenta institucional,
          // donde todos sus centros aparecerían marcados por falta del dato.
          const lab = puedeClasificar && !esComercial(s);
          return (
            <Link key={s.id} to={`/centro-v3/${s.id}`} data-testid={`centro-sel-${s.id}`}
              className={`flex min-w-0 items-center justify-between gap-2 rounded-xl border bg-white p-4 hover:shadow-sm ${lab ? "border-[#9B2C2C]/30 hover:border-[#9B2C2C]" : "border-slate-200 hover:border-[#01CBE3]"}`}>
              <div className="min-w-0">
                <p className="flex min-w-0 items-center gap-2 font-bold text-slate-800">
                  <span className="truncate">{s.nombre}</span>
                  {lab && <span data-testid="centro-lab-badge" className="shrink-0 rounded bg-[#9B2C2C]/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#9B2C2C]">Laboratorio</span>}
                </p>
                <p className="truncate text-xs text-slate-500">{s.num_personas ?? "—"} {s.num_personas === 1 ? "residente" : "residentes"}{s.ciudad ? ` · ${s.ciudad}` : ""}</p>
              </div>
              <ChevronRight className="h-5 w-5 shrink-0 text-slate-400" />
            </Link>
          );
        })}
      </div>
      {visibles.length === 0 && (
        fallo
          ? <p data-testid="centro-sel-error" className="mt-6 rounded-xl border border-dashed border-indigo-200 bg-indigo-50 p-8 text-center text-sm text-indigo-700">No se pudo consultar la lista de centros. Reintenta; no hay estado que mostrar.</p>
          : sitios.length > 0
            ? <p data-testid="centro-sel-solo-lab" className="mt-6 rounded-xl border border-dashed border-slate-200 bg-white p-8 text-center text-slate-400">No hay centros comerciales para mostrar. Hay {laboratorio.length} de laboratorio: usa «Ver laboratorio».</p>
            : <p data-testid="centro-sel-vacio" className="mt-6 rounded-xl border border-dashed border-slate-200 bg-white p-8 text-center text-slate-400">
                {institucional
                  ? "No tienes ningún centro autorizado para monitorear. Pídele a tu administración que te conceda acceso a tu centro."
                  : "No tienes centros autorizados para el radar."}
              </p>
      )}
    </div>
  );
}

export default function CentroV3() {
  const { sitioId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const caps = capacidades(user);
  // v2.13.3 — el monitorista interno opera el universo residencial de Galarya: su radar es
  // PERSONA-céntrico. El Radar del Centro V3 (un sitio por pantalla) se conserva intacto para las
  // cuentas institucionales externas y para el supervisor, que mantiene su acceso global.
  const radarPersonas = caps.interno && !caps.supervisor;
  const nowMs = useNow();
  const { scenario, demo, isDemo } = useScenario();
  const [detalle, setDetalle] = useState(null);
  const [alarmas, setAlarmas] = useState([]);
  const [listaParcial, setListaParcial] = useState("");  // "" = cola recorrida entera
  const ticketRef = useRef(0);
  const [cs, setCs] = useState([]);
  const [cob, setCob] = useState([]);
  const [err, setErr] = useState(false);
  const [cobFail, setCobFail] = useState(false);
  const [csFail, setCsFail] = useState(false);
  const [lastOk, setLastOk] = useState(0);
  const [selId, setSelId] = useState(null);
  const started = useRef(false);
  const demoInst = scenario === "demo_inst";

  const load = useCallback(async () => {
    // H4 · una cuenta EXTERNA no consulta aquí: su centro se pinta con RadarInstitucional (abajo) y
    // `/cobertura`, `/ekh/current-state` y `/sitios/{id}` son rutas INTERNAS (403 garantizado).
    if (!sitioId || caps.externo) return;
    const ticket = (ticketRef.current += 1);
    const [d, a, c, cv] = await Promise.allSettled([
      api.get(`/sitios/${sitioId}`), getAlarmas(),
      api.get(`/ekh/current-state`, { params: { sitio_id: sitioId } }), api.get(`/cobertura`),
    ]);
    if (ticket !== ticketRef.current) return;   // respuesta vieja: no pisa una más reciente
    if (d.status === "fulfilled") { setDetalle(d.value.data); setErr(false); setLastOk(Date.now()); }
    else { setErr(true); return; }
    if (a.status === "fulfilled") {
      setAlarmas(a.value);
      setListaParcial(a.value?.incompleta ? textoListaIncompleta(a.value) : "");
    }
    if (c.status === "fulfilled") { setCs(c.value.data); setCsFail(false); }
    else setCsFail(true);   // RADAR-002: un error de consulta NO puede quedar invisible
    if (cv.status === "fulfilled") { setCob(cv.value.data.cobertura || []); setCobFail(false); }
    else setCobFail(true);
  }, [sitioId, caps.externo]);

  useEffect(() => {
    if (isDemo || !sitioId || caps.externo) return;
    load(); started.current = true;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [isDemo, sitioId, load, caps.externo]);
  useEffect(() => { setSelId(null); }, [scenario, sitioId]);

  const vistaResidencial = radarPersonas && !isDemo;

  const vista = useMemo(() => {
    if (isDemo) return null;   // v2.14.1 — los demos se pintan con el Radar Operativo (abajo).
    return detalle ? construirVista(detalle, alarmas, cs, cob, nowMs, { csFail, cobFail }) : null;
  }, [isDemo, detalle, alarmas, cs, cob, nowMs, csFail, cobFail]);

  // v2.14.1 DEMO PARITY — el fixture DEMO se proyecta al MISMO contrato de los read-models de
  // v2.14.0 para poder reutilizar la UI vigente sin duplicar componentes ni tocar el backend.
  const demoSnap = useMemo(() => {
    if (!isDemo || !demo) return null;
    return demoInst ? snapshotInstitucional(demo) : snapshotResidencial(demo);
  }, [isDemo, demo, demoInst]);

  const live = isDemo ? true : (!err && nowMs - lastOk < 9000);
  const selPersona = vista ? vista.grupos.flatMap((g) => g.personas).find((p) => p.id === selId) || null : null;

  // RADAR-005: si la tarjeta ya muestra una alerta activa, el recorrido operativo abre ESE caso
  // (deep-link ?caso= de la Central, que ya existía) en lugar de la ficha. No se crean ni cierran
  // alarmas aquí: sólo se navega a un caso que YA existe (igual en DEMO y en REAL).
  const abrirDesdeTarjeta = useCallback((p) => {
    const aid = p?.alarma?.id;
    if (aid) navigate(`/consola-v3?caso=${encodeURIComponent(aid)}`);
    else setSelId(p.id);
  }, [navigate]);

  const instSource = useCallback(async (query) => {
    if (demoInst && demo) return demo.poblacion.filter((t) => t.nombre.toLowerCase().includes(query.toLowerCase())).map((t) => ({ id: t.id, nombre: t.nombre, edad: t.edad, sitio_nombre: t.sitio_nombre, caso: !!t.atencion }));
    const { data } = await api.get("/personas", { params: { q: query, sitio_id: sitioId } });
    return (data.personas || []).map((p) => ({ id: p.id, nombre: p.nombre, edad: p.edad, sitio_nombre: p.sitio_nombre, caso: !!p.caso_activo }));
  }, [demoInst, demo, sitioId]);

  // v2.14.0 — el monitorista interno residencial opera desde el RADAR OPERATIVO (persona-céntrico).
  if (vistaResidencial) return <RadarOperativo />;

  // v2.14.0 — la cuenta INSTITUCIONAL opera su centro desde el RADAR OPERATIVO · INSTITUCIONAL.
  // Causa del problema que motivó este tramo: esta pantalla resolvía el centro con endpoints
  // INTERNOS (`/sitios/{id}` con DTO recortado —sin dispositivos ni habitación/cama—, `/cobertura`
  // y `/ekh/current-state`, ambos 403 para external), así que una monitorista institucional no
  // podía ver ni ubicación ni cobertura. Ahora usa el read-model scoped del Radar. El recorrido
  // del supervisor interno (abajo) queda EXACTAMENTE igual.
  if (caps.externo && !isDemo) {
    if (sitioId) return <RadarInstitucional sitioId={sitioId} />;
    return (
      <div data-testid="centro-v3-selector-page" className="gv2 tema-noche-auto h-full overflow-y-auto">
        <Selector institucional />
      </div>
    );
  }

  // v2.14.1 DEMO PARITY — CAUSA del gap: los dos demos históricos se pintaban con las superficies
  // anteriores (CentroGrid/`construirVista` el institucional y PoblacionSurface «Cartera
  // Residencial» el residencial), que son pre-v2.14.0. Ahora ambos consumen EXACTAMENTE la misma
  // UI del Radar Operativo, con su fixture proyectado en memoria (0 llamadas al backend).
  if (isDemo && demoSnap) {
    return (
      <div data-testid="centro-v3-page" className="gv2 tema-noche-auto flex h-full min-h-0 flex-col">
        <BarraControl>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#9B2C2C]/15 px-2.5 py-1 text-xs font-bold text-[#9B2C2C]">
            <FlaskConical className="h-3.5 w-3.5" /> DEMO
          </span>
          <ScenarioSwitch />
        </BarraControl>
        <div data-testid="centro-demo-banner" className="shrink-0 flex flex-wrap items-center gap-2 border-b border-[#9B2C2C]/30 bg-[#9B2C2C]/10 px-5 py-1.5 text-xs font-semibold text-[#9B2C2C]">
          <FlaskConical className="h-3.5 w-3.5" /> DEMOSTRACIÓN · DATOS FICTICIOS · NO SE MUESTRA MONITOREO REAL — {demo.label} · {demo.resumen}
        </div>
        <div className="min-h-0 flex-1">
          {demoInst
            ? <RadarInstitucional snapshot={demoSnap} demoFichas={demo.fichas} />
            : <RadarOperativo snapshot={demoSnap} demoFichas={demo.fichas} />}
        </div>
      </div>
    );
  }

  // Real, sin sitio elegido → selector.
  if (!isDemo && !sitioId) {
    return (
      <div data-testid="centro-v3-selector-page" className="gv2 tema-noche-auto h-full overflow-y-auto">
        <BarraControl>
          <span className="flex items-center gap-2 text-sm font-semibold text-[#012472]"><Radar className="h-5 w-5" /> Radar del Centro</span>
          <ScenarioSwitch />
        </BarraControl>
        <Selector />
      </div>
    );
  }

  return (
    <div data-testid="centro-v3-page" className="gv2 tema-noche-auto h-full overflow-y-auto">
      <div className="sticky top-0 z-20 border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-2.5">
          <div className="flex items-center gap-3">
            {!isDemo
              ? <button data-testid="centro-volver" onClick={() => navigate("/centro-v3")} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#012472]"><Radar className="h-5 w-5" /> Radar del Centro</button>
              : <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#012472]"><Radar className="h-5 w-5" /> Radar del Centro</span>}
            {isDemo ? (
              <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold bg-[#9B2C2C]/15 text-[#9B2C2C]">
                <FlaskConical className="h-3.5 w-3.5" /> DEMO
              </span>
            ) : (
              // RADAR-002: el indicador describe la ACTUALIZACIÓN DE LA VISTA, no la salud ni la
              // cobertura de los sensores (eso se informa por tarjeta y en los avisos de abajo).
              <span data-testid="centro-vista-estado"
                title={live
                  ? "La vista se actualiza automáticamente. No implica que la cobertura de los sensores esté confirmada."
                  : "La vista dejó de actualizarse. Lo que ves puede no reflejar la situación actual."}
                className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold ${live ? "bg-[#01CBE3]/15 text-[#0891A6]" : "bg-red-100 text-red-700"}`}>
                {live ? <Radio className="h-3.5 w-3.5" /> : <WifiOff className="h-3.5 w-3.5" />}
                {live ? (csFail || cobFail ? "VISTA EN VIVO · DATOS PARCIALES" : "VISTA EN VIVO") : "VISTA SIN ACTUALIZAR"}
              </span>
            )}
          </div>
          <div className="flex min-w-0 flex-wrap items-center gap-2"><PersonaQuickSearch source={instSource} onOpen={(r) => setSelId(r.id)} resetKey={`${scenario}-${sitioId || ""}`} /><ScenarioSwitch /></div>
        </div>
        {isDemo && (
          <div data-testid="centro-demo-banner" className="flex flex-wrap items-center gap-2 border-t border-[#9B2C2C]/30 bg-[#9B2C2C]/10 px-5 py-1.5 text-xs font-semibold text-[#9B2C2C]">
            <FlaskConical className="h-3.5 w-3.5" /> DEMOSTRACIÓN · DATOS FICTICIOS · NO SE MUESTRA MONITOREO REAL — {demo.label} · {demo.resumen}
          </div>
        )}
        {vista && <div className="px-5 py-3"><CentroHeader sitio={vista.sitio} header={vista.header} /></div>}
      </div>

      {!vista ? (
        <div className="p-10 text-center text-slate-400">{err ? "No se pudo cargar el centro. Reintentando…" : "Cargando centro…"}</div>
      ) : (
        <div className="gv2-dock-space mx-auto max-w-[1600px] space-y-5 p-5">
          {vista.atencion.length > 0 && <AtencionList atencion={vista.atencion} onAbrirCentral={() => navigate("/consola-v3")} highlightNombre={selPersona && selPersona.alarma ? selPersona.nombre : null} />}
          {/* H1 · el supervisor interno autorizado ve el MISMO mosaico del centro (mismo componente
              y mismo endpoint acotado por sitio). No se altera RBAC: el backend sigue exigiendo
              site.read sobre este sitio. */}
          {!isDemo && sitioId && <MosaicoEkh sitioId={sitioId} />}
      {!isDemo && listaParcial && <div data-testid="centro-lista-incompleta" className="rounded-xl border border-amber-400 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-900">Lista incompleta — {listaParcial}</div>}
          {!isDemo && csFail && <div data-testid="centro-cs-nc" className="rounded-xl border border-indigo-300 bg-indigo-50 px-3 py-2 text-sm font-medium text-indigo-800">Lecturas no confirmables — no se pudo consultar el estado actual de los sensores. Lo que ves puede no reflejar la situación actual; no hay confirmación operativa.</div>}
          {!isDemo && cobFail && <div data-testid="centro-cob-nc" className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800">Cobertura no confirmable — no se pudo consultar /cobertura. El resto del radar sigue disponible.</div>}
          <CoberturasResumen key={`${scenario}-${sitioId}`} coberturas={vista.coberturas} historicas={!!vista.coberturasHistoricas} />
          {vista.grupos.every((g) => g.personas.length === 0) ? (
            <p data-testid="centro-vacio" className="rounded-xl border border-dashed border-slate-200 bg-white p-10 text-center text-slate-400">Este centro no tiene residentes registrados.</p>
          ) : <CentroGrid grupos={vista.grupos} total={vista.header.total} onSelect={abrirDesdeTarjeta} onAbrirFicha={(p) => setSelId(p.id)} selectedId={selId} />}
        </div>
      )}
      {selPersona && <PersonaDrawer open onClose={() => setSelId(null)} isDemo={demoInst} demoFicha={demoInst && demo ? demo.fichas[selId] : null} personaId={demoInst ? null : selId} />}
    </div>
  );
}
